import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { createBooking, listSpeakers } from '../lib/api.js'
import ErrorAlert from '../components/ErrorAlert.jsx'
import useMe from '../hooks/useMe.js'
import { PAGE_FORM } from '../lib/layout.js'
import { BTN_PRIMARY_BLOCK, INPUT_BLOCK } from '../lib/ui.js'


const expenseOptions = [
  { value: '', label: '— select —' },
  { value: 'ORGANISER', label: 'Organiser pays' },
  { value: 'SPEAKER', label: 'Speaker pays' },
  { value: 'SHARED', label: 'Shared' },
]

export default function BookSpeakerPage() {
  // Signed-in viewers / speakers / admins get their organiser fields pre-
  // filled from /me (#95). Anonymous visitors see blank fields. Both forms
  // remain editable.
  const { me } = useMe()
  const [speakers, setSpeakers] = useState([])
  const [selectedSpeakers, setSelectedSpeakers] = useState([])

  const [eventName, setEventName] = useState('')
  const [organiserName, setOrganiserName] = useState('')
  const [organiserEmail, setOrganiserEmail] = useState('')
  const [organiserOrg, setOrganiserOrg] = useState('')
  const [startsOn, setStartsOn] = useState('')
  const [endsOn, setEndsOn] = useState('')
  const [city, setCity] = useState('')
  const [country, setCountry] = useState('')
  const [format, setFormat] = useState('')
  const [audienceSize, setAudienceSize] = useState('')
  const [topic, setTopic] = useState('')
  const [travelPaidBy, setTravelPaidBy] = useState('')
  const [lodgingPaidBy, setLodgingPaidBy] = useState('')
  const [honorariumPaidBy, setHonorariumPaidBy] = useState('')
  const [honorariumAmount, setHonorariumAmount] = useState('')
  const [honorariumCurrency, setHonorariumCurrency] = useState('USD')
  const [otherTerms, setOtherTerms] = useState('')
  const [notes, setNotes] = useState('')
  const [hp, setHp] = useState('') // honeypot

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [done, setDone] = useState(null)

  useEffect(() => { listSpeakers().then(setSpeakers).catch(() => {}) }, [])

  // Prefill organiser fields once /me resolves. Don't overwrite if the user
  // already typed something — only prefill when the field is still blank.
  useEffect(() => {
    if (!me) return
    setOrganiserName((cur) => cur || me.displayName || '')
    setOrganiserEmail((cur) => cur || me.email || '')
  }, [me])

  const toggleSpeaker = (uid) => {
    setSelectedSpeakers((prev) =>
      prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]
    )
  }

  const submit = async (e) => {
    e.preventDefault()
    if (selectedSpeakers.length === 0) {
      setError('Pick at least one speaker.')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const payload = {
        eventName, organiserName, organiserEmail,
        speakerUserIds: selectedSpeakers,
        ...(organiserOrg && { organiserOrg }),
        ...(startsOn && { startsOn }),
        ...(endsOn && { endsOn }),
        ...(city && { city }),
        ...(country && { country }),
        ...(format && { format }),
        ...(audienceSize && { audienceSize: Number(audienceSize) }),
        ...(topic && { topic }),
        ...(travelPaidBy && { travelPaidBy }),
        ...(lodgingPaidBy && { lodgingPaidBy }),
        ...(honorariumPaidBy && { honorariumPaidBy }),
        ...(honorariumAmount && { honorariumAmount: Number(honorariumAmount) }),
        ...(honorariumCurrency && { honorariumCurrency }),
        ...(otherTerms && { otherTerms }),
        ...(notes && { notes }),
        ...(hp && { hp }),
      }
      const created = await createBooking(payload)
      setDone(created)
    } catch (err) {
      setError(err.message || 'Booking failed.')
    } finally {
      setLoading(false)
    }
  }

  if (done) {
    return (
      <main className={`${PAGE_FORM} pb-12 pt-12`}>
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200/60 sm:p-8">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Thanks — request received</h1>
          <p className="mt-2 text-sm text-slate-600">
            We received your request for <span className="font-medium">{done.eventName}</span>.
            We'll be in touch at <span className="font-medium">{done.organiserEmail}</span>.
          </p>
          <p className="mt-3 text-xs text-slate-500">Reference: <code>{done.id}</code></p>
          <Link
            to="/"
            className={`${BTN_PRIMARY_BLOCK} mt-5`}
          >
            Back to calendar
          </Link>
        </div>
      </main>
    )
  }

  return (
    <main className={`${PAGE_FORM} pb-12 pt-8 sm:pt-10`}>
      <header className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          <span className="brand-gradient-text">Book a speaker</span>
        </h1>
        <p className="mt-2 text-base text-slate-600">
          Tell us about your event and which speakers you'd like. We'll get back to you within a few days.
        </p>
      </header>

      <form onSubmit={submit} className="space-y-4 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200/60 sm:p-6" noValidate>
        {/* Honeypot — hidden from users, bots fill it. */}
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={hp}
          onChange={(e) => setHp(e.target.value)}
          aria-hidden="true"
          className="hidden"
          name="website"
        />

        <fieldset className="space-y-3">
          <legend className="text-sm font-semibold text-slate-800">Speakers</legend>
          {speakers.length === 0 ? (
            <p className="text-sm text-slate-500">Loading speakers…</p>
          ) : (
            <ul className="grid gap-2 sm:grid-cols-2">
              {speakers.map((s) => {
                const checked = selectedSpeakers.includes(s.userId)
                return (
                  <li key={s.userId}>
                    <label className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition ${
                      checked ? 'border-brand-400 bg-brand-50' : 'border-slate-200 hover:bg-slate-50'
                    }`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSpeaker(s.userId)}
                        className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                      />
                      <span className="font-medium">{s.displayName || s.email || `User #${s.userId}`}</span>
                      {s.role ? <span className="ml-auto text-xs text-slate-500">{s.role.toLowerCase()}</span> : null}
                    </label>
                  </li>
                )
              })}
            </ul>
          )}
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="text-sm font-semibold text-slate-800">Event</legend>
          <label className="block text-sm">
            <span className="block font-medium text-slate-700">Event name</span>
            <input type="text" required value={eventName} onChange={(e) => setEventName(e.target.value)} className={INPUT_BLOCK} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="block font-medium text-slate-700">Starts</span>
              <input type="date" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} className={INPUT_BLOCK} />
            </label>
            <label className="block text-sm">
              <span className="block font-medium text-slate-700">Ends</span>
              <input type="date" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} className={INPUT_BLOCK} />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="block font-medium text-slate-700">City</span>
              <input type="text" value={city} onChange={(e) => setCity(e.target.value)} className={INPUT_BLOCK} />
            </label>
            <label className="block text-sm">
              <span className="block font-medium text-slate-700">Country</span>
              <input type="text" value={country} onChange={(e) => setCountry(e.target.value)} placeholder="e.g. US" className={INPUT_BLOCK} />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="block font-medium text-slate-700">Format</span>
              <select value={format} onChange={(e) => setFormat(e.target.value)} className={INPUT_BLOCK}>
                <option value="">—</option>
                <option value="IN_PERSON">In person</option>
                <option value="VIRTUAL">Virtual</option>
                <option value="HYBRID">Hybrid</option>
              </select>
            </label>
            <label className="block text-sm">
              <span className="block font-medium text-slate-700">Audience size</span>
              <input type="number" min={0} value={audienceSize} onChange={(e) => setAudienceSize(e.target.value)} className={INPUT_BLOCK} />
            </label>
          </div>
          <label className="block text-sm">
            <span className="block font-medium text-slate-700">Topic / abstract</span>
            <textarea rows={3} value={topic} onChange={(e) => setTopic(e.target.value)} className={INPUT_BLOCK} />
          </label>
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="text-sm font-semibold text-slate-800">Expenses</legend>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="block text-sm">
              <span className="block font-medium text-slate-700">Travel paid by</span>
              <select value={travelPaidBy} onChange={(e) => setTravelPaidBy(e.target.value)} className={INPUT_BLOCK}>
                {expenseOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <label className="block text-sm">
              <span className="block font-medium text-slate-700">Lodging paid by</span>
              <select value={lodgingPaidBy} onChange={(e) => setLodgingPaidBy(e.target.value)} className={INPUT_BLOCK}>
                {expenseOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <label className="block text-sm">
              <span className="block font-medium text-slate-700">Honorarium paid by</span>
              <select value={honorariumPaidBy} onChange={(e) => setHonorariumPaidBy(e.target.value)} className={INPUT_BLOCK}>
                {expenseOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <label className="col-span-2 block text-sm">
              <span className="block font-medium text-slate-700">Honorarium amount <span className="text-slate-400 font-normal">(optional)</span></span>
              <input type="number" min={0} step="0.01" value={honorariumAmount} onChange={(e) => setHonorariumAmount(e.target.value)} className={INPUT_BLOCK} />
            </label>
            <label className="block text-sm">
              <span className="block font-medium text-slate-700">Currency</span>
              <input type="text" maxLength={8} value={honorariumCurrency} onChange={(e) => setHonorariumCurrency(e.target.value)} className={INPUT_BLOCK} />
            </label>
          </div>
          <label className="block text-sm">
            <span className="block font-medium text-slate-700">Other terms <span className="text-slate-400 font-normal">(optional)</span></span>
            <textarea rows={2} value={otherTerms} onChange={(e) => setOtherTerms(e.target.value)} className={INPUT_BLOCK} />
          </label>
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="text-sm font-semibold text-slate-800">You</legend>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="block font-medium text-slate-700">Your name</span>
              <input type="text" required value={organiserName} onChange={(e) => setOrganiserName(e.target.value)} className={INPUT_BLOCK} />
            </label>
            <label className="block text-sm">
              <span className="block font-medium text-slate-700">Email</span>
              <input type="email" required value={organiserEmail} onChange={(e) => setOrganiserEmail(e.target.value)} className={INPUT_BLOCK} />
            </label>
          </div>
          <label className="block text-sm">
            <span className="block font-medium text-slate-700">Organisation <span className="text-slate-400 font-normal">(optional)</span></span>
            <input type="text" value={organiserOrg} onChange={(e) => setOrganiserOrg(e.target.value)} className={INPUT_BLOCK} />
          </label>
          <label className="block text-sm">
            <span className="block font-medium text-slate-700">Anything else?</span>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className={INPUT_BLOCK} />
          </label>
        </fieldset>

        {error ? <ErrorAlert title="Couldn't submit booking" message={error} source="BookSpeakerPage" /> : null}

        <button
          type="submit"
          disabled={loading || selectedSpeakers.length === 0}
          className={BTN_PRIMARY_BLOCK}
        >
          {loading ? 'Submitting…' : 'Submit booking request'}
        </button>

        <Link to="/" className="block text-center text-sm text-slate-500 hover:text-slate-700">
          ← Back to calendar
        </Link>
      </form>
    </main>
  )
}
